import { round2 } from "utils/fixedAmountDiscountAllocation";

export function orderLineKey(productId: string, variantId?: string): string {
  return `${String(productId)}:${String(variantId ?? "")}`;
}

export function getOriginalCatalogUnitPrice(product: {
  unit_price?: number;
  retail_price?: number;
  unit_retail_price?: number;
}): number {
  return round2(
    Number(product.unit_price ?? product.unit_retail_price ?? product.retail_price ?? 0)
  );
}

/** Actual/base unit price (view file: unit_price ?? unit_retail_price). */
export function getInstallationLineActualUnitPrice(
  product: {
    unit_price?: number;
    retail_price?: number;
    unit_retail_price?: number;
  },
  resolvedLine?: { unit_price?: number } | null
): number {
  if (resolvedLine?.unit_price != null && Number(resolvedLine.unit_price) > 0) {
    return round2(Number(resolvedLine.unit_price));
  }
  return getOriginalCatalogUnitPrice(product);
}

/**
 * Resolve request + create payload: manual override sends entered amount;
 * otherwise send original catalog/base price (qty-driven volume pricing is backend-only).
 */
export function getResolveRequestUnitPrice(
  lineKey: string,
  originalUnitPrice: number,
  manualUnitPriceMap: Record<string, number>
): number {
  if (manualUnitPriceMap[lineKey] !== undefined) {
    return round2(Number(manualUnitPriceMap[lineKey]));
  }
  return round2(originalUnitPrice);
}

/** Post-discount unit price from resolve (internal / payload use). */
export function getDisplayUnitCost(
  lineKey: string,
  originalUnitPrice: number,
  manualUnitPriceMap: Record<string, number>,
  resolvedChangedUnitPrice?: number | null
): number {
  if (manualUnitPriceMap[lineKey] !== undefined) {
    return round2(Number(manualUnitPriceMap[lineKey]));
  }
  if (resolvedChangedUnitPrice != null && Number.isFinite(Number(resolvedChangedUnitPrice))) {
    return round2(Number(resolvedChangedUnitPrice));
  }
  return round2(originalUnitPrice);
}

/**
 * Unit Cost column / line total: changed_unit_price × qty.
 * Manual override uses entered per-unit price × qty.
 */
export function getInstallationLineChangedUnitCostTotal(
  lineKey: string,
  product: { quantity?: number; changed_unit_price?: number; unit_price?: number },
  manualUnitPriceMap: Record<string, number>,
  resolvedLine?: { changed_unit_price?: number; unit_price?: number } | null
): number {
  const qty = Math.max(1, Number(product.quantity) || 1);
  if (manualUnitPriceMap[lineKey] !== undefined) {
    return round2(Number(manualUnitPriceMap[lineKey]) * qty);
  }
  const changedUnitPrice = Number(
    resolvedLine?.changed_unit_price ??
      product.changed_unit_price ??
      resolvedLine?.unit_price ??
      product.unit_price ??
      0
  );
  return round2(changedUnitPrice * qty);
}

/**
 * Unit Cost column:
 * - Case 1: actual/catalog price (NOT changed_unit_price)
 * - Case 2 (manual edit): user-entered price
 */
export function getInstallationLineUnitCostDisplay(
  lineKey: string,
  product: { unit_price?: number; retail_price?: number; unit_retail_price?: number },
  manualUnitPriceMap: Record<string, number>,
  resolvedLine?: { unit_price?: number } | null
): number {
  if (manualUnitPriceMap[lineKey] !== undefined) {
    return round2(Number(manualUnitPriceMap[lineKey]));
  }
  return getInstallationLineActualUnitPrice(product, resolvedLine);
}

/**
 * Per-product discount column — volume / assignment discount only (not coupon).
 * - Case 1: (catalog − changed_unit_price) × qty − line coupon (discount_on_total_quantity)
 * - Case 2 (manual edit): 0
 */
export function getInstallationLineDiscountDisplay(
  lineKey: string,
  product: {
    unit_price?: number;
    retail_price?: number;
    unit_retail_price?: number;
    quantity?: number;
    changed_unit_price?: number;
    discount?: number;
    discount_on_total_quantity?: number;
    discount_code?: string;
  },
  manualUnitPriceMap: Record<string, number>,
  resolvedLine?: {
    discount_on_unit_quantity?: number;
    discount_on_total_quantity?: number;
    discount?: number;
    discount_code?: string;
    unit_price?: number;
    changed_unit_price?: number;
  } | null
): number {
  if (manualUnitPriceMap[lineKey] !== undefined) {
    return 0;
  }

  const qty = Math.max(1, Number(product.quantity) || 1);
  const catalog = getInstallationLineActualUnitPrice(product, resolvedLine);
  const changed = Number(
    resolvedLine?.changed_unit_price ??
      product.changed_unit_price ??
      resolvedLine?.unit_price ??
      product.unit_price ??
      catalog
  );

  const volumeFromPrices = round2(Math.max(0, (catalog - changed) * qty));
  if (volumeFromPrices > 0) return volumeFromPrices;

  const lineDisc = round2(
    Number(
      resolvedLine?.discount_on_total_quantity ??
        resolvedLine?.discount ??
        product.discount_on_total_quantity ??
        product.discount ??
        0
    )
  );

  const hasProductCouponOnLine = Boolean(
    String(resolvedLine?.discount_code ?? product.discount_code ?? "").trim()
  );

  if (lineDisc > 0 && !hasProductCouponOnLine) return lineDisc;

  return 0;
}

/**
 * Units Cost for cost breakdown: Σ (original catalog unit price × qty − volume discount only).
 * Coupon / order discount is excluded — shown separately in the breakdown.
 */
export function computeInstallationUnitsCostAfterVolumeDiscount(args: {
  catalogProducts: Array<{
    product_id?: string;
    variant_id?: string;
    quantity?: number;
    unit_price?: number;
    retail_price?: number;
    unit_retail_price?: number;
    changed_unit_price?: number;
    discount?: number;
    discount_on_total_quantity?: number;
  }>;
  customProducts?: Array<{ quantity?: number; Price?: number }>;
  manualUnitPriceMap: Record<string, number>;
  getResolvedLineItem: (p: {
    product_id: string;
    variant_id?: string;
  }) => {
    unit_price?: number;
    changed_unit_price?: number;
    discount_on_total_quantity?: number;
    discount?: number;
    discount_on_unit_quantity?: number;
  } | null | undefined;
}): number {
  let total = 0;

  for (const p of args.catalogProducts || []) {
    if (!p.product_id) continue;
    const key = orderLineKey(p.product_id, p.variant_id);
    const qty = Math.max(1, Number(p.quantity) || 1);
    const resolved = args.getResolvedLineItem({
      product_id: p.product_id,
      variant_id: p.variant_id,
    });

    if (args.manualUnitPriceMap[key] !== undefined) {
      total += round2(Number(args.manualUnitPriceMap[key]) * qty);
      continue;
    }

    const catalog = getInstallationLineActualUnitPrice(p, resolved);
    const volumeDiscount = getInstallationLineDiscountDisplay(
      key,
      p,
      args.manualUnitPriceMap,
      resolved ?? undefined
    );
    total += round2(Math.max(0, catalog * qty - volumeDiscount));
  }

  for (const cp of args.customProducts || []) {
    total += round2(Number(cp.Price || 0) * Math.max(1, Number(cp.quantity) || 1));
  }

  return round2(total);
}

/**
 * Units Cost for cost breakdown: Σ (changed_unit_price × qty).
 * Volume pricing is already in changed_unit_price; coupon discount is shown separately.
 */
export function computeInstallationUnitsCostFromChangedUnitPrice(args: {
  products: Array<{
    product_id?: string;
    variant_id?: string;
    quantity?: number;
    unit_price?: number;
    changed_unit_price?: number;
    inventory_type?: string;
    inventory_source?: string;
    Price?: number;
  }>;
  manualUnitPriceMap: Record<string, number>;
  getResolvedLineItem: (p: {
    product_id: string;
    variant_id?: string;
  }) => {
    changed_unit_price?: number;
    unit_price?: number;
  } | null | undefined;
  isCustomProduct?: (product: {
    inventory_type?: string;
    inventory_source?: string;
  }) => boolean;
}): number {
  let total = 0;

  for (const p of args.products || []) {
    const qty = Math.max(1, Number(p.quantity) || 1);
    const isCustom = args.isCustomProduct?.(p) ?? false;

    if (isCustom) {
      total += round2(Number(p.Price ?? p.changed_unit_price ?? p.unit_price ?? 0) * qty);
      continue;
    }

    if (!p.product_id) continue;
    const key = orderLineKey(p.product_id, p.variant_id);
    const resolved = args.getResolvedLineItem({
      product_id: p.product_id,
      variant_id: p.variant_id,
    });

    if (args.manualUnitPriceMap[key] !== undefined) {
      total += round2(Number(args.manualUnitPriceMap[key]) * qty);
      continue;
    }

    const changedUnitPrice = Number(
      resolved?.changed_unit_price ??
        p.changed_unit_price ??
        resolved?.unit_price ??
        p.unit_price ??
        0
    );
    total += round2(changedUnitPrice * qty);
  }

  return round2(total);
}

/** Units Cost for cost breakdown — uses manual override when set, else resolved line totals. */
export function computeInstallationUnitsCost(args: {
  catalogProducts: Array<{
    is_new_catalog?: boolean;
    product_id?: string;
    variant_id?: string;
    quantity?: number;
    unit_price?: number;
    retail_price?: number;
    unit_retail_price?: number;
  }>;
  customProducts: Array<{ quantity?: number; Price?: number }>;
  manualUnitPriceMap: Record<string, number>;
  unitPriceDraftMap?: Record<string, string>;
  getResolvedLineItem: (p: {
    product_id: string;
    variant_id?: string;
  }) => {
    item_sub_total?: number;
    changed_unit_price?: number;
    unit_price?: number;
  } | null | undefined;
}): number {
  let total = 0;

  for (const p of args.catalogProducts) {
    if (!p.is_new_catalog || !p.product_id) continue;
    const key = orderLineKey(p.product_id, p.variant_id);
    const qty = Math.max(1, Number(p.quantity) || 1);
    const manual = args.manualUnitPriceMap[key];
    if (manual !== undefined) {
      total += round2(manual * qty);
      continue;
    }
    const draftRaw = args.unitPriceDraftMap?.[key];
    if (draftRaw !== undefined) {
      const trimmed = draftRaw.trim();
      if (trimmed !== "" && trimmed !== ".") {
        const draftPrice = Number(trimmed);
        if (Number.isFinite(draftPrice) && draftPrice >= 0) {
          total += round2(draftPrice * qty);
          continue;
        }
      }
    }
    const li = args.getResolvedLineItem({
      product_id: p.product_id,
      variant_id: p.variant_id,
    });
    total += round2(
      Number(
        li?.item_sub_total ??
          Number(li?.changed_unit_price ?? li?.unit_price ?? getOriginalCatalogUnitPrice(p)) *
            qty
      )
    );
  }

  for (const cp of args.customProducts || []) {
    total += round2(Number(cp.Price || 0) * Math.max(1, Number(cp.quantity) || 1));
  }

  return round2(total);
}

/** Grand total from cost-breakdown components (matches UI breakdown rows). */
export function computeInstallationGrandTotal(args: {
  subtotalAmount: number;
  specialFeeSubtotal: number;
  tax: number;
  discountAmount: number;
}): number {
  return round2(
    Math.max(
      0,
      Number(args.subtotalAmount || 0) +
        Number(args.specialFeeSubtotal || 0) +
        Number(args.tax || 0) -
        Number(args.discountAmount || 0)
    )
  );
}

/** True when the user explicitly overrode unit price (not default catalog pricing). */
export function isManualUnitPriceLine(li: {
  is_new_purchase?: boolean;
  manual_unit_price_override?: boolean;
  is_price_overridden?: boolean;
}): boolean {
  return (
    li?.is_new_purchase === true &&
    (li.manual_unit_price_override === true || li.is_price_overridden === true)
  );
}
